// NotificationDropdown.jsx — Customer Navbar Dropdown
import { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppContext from '../../../context/AppContext';
import '../../../assests/css/customer/NotificationDropdown.css';

const formatTime = (raw) => {
  if (!raw) return '';
  const date = new Date(raw);
  if (isNaN(date)) return raw;
  const diff = Math.floor((Date.now() - date) / 1000);
  if (diff < 60)    return 'Just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString();
};

const iconMap = {
  order:   '📦',
  warning: '⚠️',
  review:  '⭐',
  payment: '💳',
  system:  '⚙️',
  package: '📦',
  message: '💬',
  bell:    '🔔',
  cart:    '🛒',
  user:    '👤',
};

const accentByIcon = (icon) => {
  const map = {
    order:   { accent: '#2563EB', accentBg: 'rgba(37,99,235,0.08)'   },
    warning: { accent: '#D97706', accentBg: 'rgba(217,119,6,0.08)'   },
    review:  { accent: '#8B7355', accentBg: 'rgba(139,115,85,0.1)'   },
    payment: { accent: '#059669', accentBg: 'rgba(5,150,105,0.08)'   },
    system:  { accent: '#2563EB', accentBg: 'rgba(37,99,235,0.08)'   },
    package: { accent: '#ef4444', accentBg: 'rgba(239,68,68,0.08)'   },
    message: { accent: '#8b5cf6', accentBg: 'rgba(139,92,246,0.08)'  },
    bell:    { accent: '#6b7280', accentBg: 'rgba(107,114,128,0.08)' },
    cart:    { accent: '#8b5cf6', accentBg: 'rgba(139,92,246,0.08)'  },
    user:    { accent: '#2563EB', accentBg: 'rgba(37,99,235,0.08)'   },
  };
  return map[icon] || { accent: '#6b7280', accentBg: 'rgba(107,114,128,0.08)' };
};

const mapItem = (n) => ({
  id:      n.id,
  title:   n.title       || 'Notification',
  message: n.description || '',
  detail:  n.detail      || n.description || '',
  time:    formatTime(n.created_at),
  icon:    iconMap[n.icon] || '🔔',
  unread:  n.status === 'unread',
  ...accentByIcon(n.icon || 'bell'),
});

// ── Per-item component ────────────────────────────────────────
const NotificationItem = ({ notification, onView, onDismiss }) => {
  const view    = () => onView(notification);
  const dismiss = (e) => { e.stopPropagation(); onDismiss(notification.id); };

  return (
    <div
      className={`nd-item${notification.unread ? ' unread' : ''}`}
      onClick={view}
      style={{ cursor: 'pointer' }}
    >
      <div
        className="nd-icon-wrap"
        style={{
          background: notification.accentBg,
          border:     `1px solid ${notification.accent}25`,
        }}
      >
        {notification.unread && <span className="nd-dot" />}
        <span>{notification.icon}</span>
      </div>
      <div className="nd-body">
        <p className="nd-item-title">{notification.title}</p>
        <p className="nd-item-msg">{notification.message}</p>
        <span className="nd-item-time">{notification.time}</span>
      </div>
      <button className="nd-dismiss" onClick={dismiss} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
};

const NotificationDropdown = ({ isOpen, onClose, onCountChange }) => {
  const { baseUrl, customer } = useContext(AppContext);
  const navigate    = useNavigate();
  const dropdownRef = useRef(null);

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);

  const unreadCount  = items.filter((n) => n.unread).length;
  const previewItems = [
    ...items.filter((n) =>  n.unread),
    ...items.filter((n) => !n.unread),
  ].slice(0, 5);

  // ── FIXED: onCountChange call — bell badge turant update hoga ──
  // Pehle yeh sirf unreadCount change pe chalta tha lekin
  // onCountChange callback stable nahi tha, ab properly sync hai
  useEffect(() => {
    if (isOpen && typeof onCountChange === 'function') {
      onCountChange(unreadCount);
    }
  }, [isOpen, unreadCount, onCountChange]);

   // ── FETCH ─────────────────────────────────────────────────
  const fetchNotifications = () => {
    if (!customer?.id) return;

    setLoading(true);
    axios
      .get(`${baseUrl}/customer/notification/getnotifications`, {
        params: { customer_id: customer.id },
      })
      .then((res) => {
        const raw = Array.isArray(res.data.notifications) ? res.data.notifications : [];
        setItems(raw.map(mapItem));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, customer]);

  // ── Outside click close ───────────────────────────────────
  useEffect(() => {
    const clickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) onClose();
    };
    if (isOpen) document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, [isOpen, onClose]);

  // ── View — Mark as read + notifications page pe navigate ──
  const viewItem = (notification) => {
    if (notification.unread) {
      // Optimistic update — UI turant respond kare
      setItems(prev =>
        prev.map(n => n.id === notification.id ? { ...n, unread: false } : n)
      );
      axios
        .post(`${baseUrl}/customer/notification/markasreadnotification`, { id: notification.id })
        .catch(err => {
          console.error('Mark-read error:', err);
          // Rollback agar API fail ho
          setItems(prev =>
            prev.map(n => n.id === notification.id ? { ...n, unread: true } : n)
          );
        });
    }

    navigate('/notifications', { state: { openId: notification.id } });
    onClose();
  };

  // ── DELETE ────────────────────────────────────────────────
  const dismissItem = (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    axios
      .post(`${baseUrl}/customer/notification/deletenotifications`, { id })
      .catch((err) => {
        console.error('Delete notification error:', err);
         fetchNotifications();
      });
  };

  const goToAll = () => {
    navigate('/notifications');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="notification-dropdown" ref={dropdownRef}>

      <div className="nd-header">
        <div className="nd-header-left">
          <h3 className="nd-title">Notifications</h3>
          {unreadCount > 0 && (
            <span className="nd-count-pill">{unreadCount} new</span>
          )}
        </div>
      </div>

      <div className="nd-list">
        {loading ? (
          <div className="nd-empty">
            <div className="nd-empty-icon">⏳</div>
            <div className="nd-empty-text">Loading...</div>
          </div>
        ) : previewItems.length === 0 ? (
          <div className="nd-empty">
            <div className="nd-empty-icon">🎉</div>
            <div className="nd-empty-text">All caught up!</div>
          </div>
        ) : (
          previewItems.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onView={viewItem}
              onDismiss={dismissItem}
            />
          ))
        )}
      </div>

      <div className="nd-footer">
        <button className="nd-view-all" onClick={goToAll}>
          View all notifications
          {items.length > 5 && ` (${items.length})`} →
        </button>
      </div>

    </div>
  );
};

export default NotificationDropdown;